import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { AccountTreeOutlined, ArrowBack, PlayArrowOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Card, CardContent, Chip, Typography } from "@mui/material";
import BackdropProgressBar from "../../../components/ui/BackdropProgressBar";
import WorkflowCanvasIcon from "../../../features/workflows/WorkflowCanvasIcon";
import WorkflowTerminalStage, {
  WorkflowTerminalStatus,
} from "../../../features/workflows/WorkflowTerminalStage";
import WorkflowRunRail from "../../../features/workflows/WorkflowRunRail";
import WorkflowRunStageBody from "../../../features/workflows/WorkflowRunStageBody";
import WorkflowTestRunBar from "../../../features/workflows/WorkflowTestRunBar";
import { PIF_EMPLOYEE_NOT_CREATED_END } from "../../../features/workflows/WorkflowCancelLive";
import { WorkflowApprovalDecided } from "../../../features/workflows/WorkflowApprovalLive";
import { renderWorkflowRunSourceScreen, WorkflowRunPage } from "../../../features/workflows/WorkflowRunPage";
import WorkflowRunFormBanner, {
  WorkflowFormFeedback,
} from "../../../features/workflows/WorkflowRunFormBanner";
import WorkflowPifApplicantBanner from "../../../features/workflows/WorkflowPifApplicantBanner";
import WorkflowPifReenterSsnCard from "../../../features/workflows/WorkflowPifReenterSsnCard";
import { WORKFLOW_CANVAS_LABELS } from "../../../features/workflows/workflowBuilderData";
import {
  PIF_LOOKUP_ERROR_MESSAGES,
  PifIdentityFlow,
  PifIdentityMatch,
  stageKeyOf,
} from "../../../features/workflows/workflowRunData";
import {
  PifStartAnswers,
  toErrorsByKey,
  toFieldIdPatch,
  toKeyedValueInputs,
  toPifStartValues,
  toPifValuesByKey,
} from "../../../features/workflows/pifCaseValues";
import { PifDetailsValues, toPifApplicantSummary } from "../../../features/workflows/pifDetailsModel";
import {
  PIF_DEFINITION_TOKEN,
  PIF_DETAILS_FIELDS,
  PIF_LATER_STAGE_KEYS,
  PIF_STAGE_KEYS,
  PIF_VARIABLES,
} from "../../../features/workflows/pifWorkflowKeys";
import {
  isOpenCase,
  PIF_STAGE_PILLS,
  toPipeline,
  toStageValueInputs,
  toStatusLabel,
  rejectedCloseReason,
  toTerminalStatus,
  webServiceStageFailed,
} from "../../../features/workflows/workflowRunState";
import { WorkflowRunCase } from "../../../hooks/useWorkflowRunInstance";
import useWorkflowRunSource from "../../../hooks/useWorkflowRunSource";
import useWorkflowFormSave from "../../../hooks/useWorkflowFormSave";
import useWorkflowPifIdentityLookup from "../../../hooks/useWorkflowPifIdentityLookup";
import useWorkflowPifApplicant from "../../../hooks/useWorkflowPifApplicant";
import useWorkflowPifDisplayNames from "../../../hooks/useWorkflowPifDisplayNames";
import { useUser } from "../../../context/UserContext";
import { useMenu } from "../../../context/MenuContext";
import useWorkflowPifResumedIdentity from "../../../hooks/useWorkflowPifResumedIdentity";
import useWorkflowRunTrace from "../../../hooks/useWorkflowRunTrace";
import { WORKFLOW_SHARE_LINK_PREFIX } from "./WorkflowShareLink";
import { ModuleNames, SEVERITIES } from "../../../data/constants";
import { useToaster } from "../../../hooks/useToaster";
import "./WorkflowRun.scss";

/** pif-3's IDs and the display names the server writes for them. */
const PIF_NAMED_IDS: [string, string][] = [
  [PIF_DETAILS_FIELDS.department, PIF_DETAILS_FIELDS.departmentName],
  [PIF_DETAILS_FIELDS.position, PIF_DETAILS_FIELDS.positionName],
  [PIF_DETAILS_FIELDS.reportsTo, PIF_DETAILS_FIELDS.reportsToName],
  [PIF_DETAILS_FIELDS.rateType, PIF_DETAILS_FIELDS.rateTypeName],
];

/** Name and time — the IP stays audit-only in v1. */
interface WorkflowDecisionStamp {
  by: string;
  at: string;
}

const WorkflowRun: React.FC = () => {
  const showToaster = useToaster();
  const { userId } = useUser();
  const { permissions } = useMenu();
  // Only a Builder editor can publish, so only they are told to (PR !32534 review).
  const canEditBuilder = permissions.some(
    (permission) => permission.moduleName === ModuleNames.WorkflowBuilder && permission.canEdit,
  );
  const source = useWorkflowRunSource();
  const { runCase, refreshCase, definition, isPifPreStart, pifStartAnswers, setPifStartAnswers } = source;

  const initialStageIndex = useMemo(() => {
    const requestedIndex = definition?.canvases.findIndex((stage) => stage.id === source.requestedStageId);
    return requestedIndex !== undefined && requestedIndex >= 0 ? requestedIndex : 0;
  }, [definition, source.requestedStageId]);

  const [stageIndex, setStageIndex] = useState(initialStageIndex);
  const [identity, setIdentity] = useState<PifIdentityMatch | null>(null);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  // The latest values, for changes that land in the same tick (pif-3 fills its defaults on show).
  const fieldValuesRef = useRef<Record<string, string>>({});
  // The terminal state comes from the case itself (finishedOnOpen below).
  const [terminalStatus, setTerminalStatus] = useState<WorkflowTerminalStatus | null>(null);
  const [decisionReason, setDecisionReason] = useState("");
  const [decisionStamp, setDecisionStamp] = useState<WorkflowDecisionStamp | null>(null);
  // The case whose stage and values are in the state above (see WorkflowRunSourceScreen).
  const [appliedCase, setAppliedCase] = useState<WorkflowRunCase | null>(null);
  const [formFeedback, setFormFeedback] = useState<WorkflowFormFeedback | null>(null);
  const [formFieldErrors, setFormFieldErrors] = useState<Record<string, string>>({});
  // The real case that was ALREADY finished when it was opened (e.g. from Instances) — read-only,
  // unlike one that finishes in this session, which offers Start another.
  const [finishedOnOpen, setFinishedOnOpen] = useState<number | null>(null);
  const firstAppliedRef = useRef<number | null>(null);
  // Set once pif-1's answers are submitted: the engine has already run pif-2 (a conditional), and
  // its read-only route panel is shown over the stage the case is now on until Continue.
  const [routedFlow, setRoutedFlow] = useState<PifIdentityFlow | null>(null);
  const [pifStartSaving, setPifStartSaving] = useState(false);
  // The pif-1 answers already sent. A Retry hands over a fresh copy, which is sent again.
  const sentPifStartRef = useRef<PifStartAnswers | null>(null);

  // A real case resumes where the engine says it is, with its saved values — a refresh mid-stage
  // lands back on the same stage with nothing lost (AC11).
  useEffect(() => {
    if (!runCase) return;
    if (firstAppliedRef.current !== runCase.instanceId) {
      firstAppliedRef.current = runCase.instanceId;
      setFinishedOnOpen(isOpenCase(runCase) ? null : runCase.instanceId);
    }
    const currentIndex = runCase.definition.canvases.findIndex((stage) => stage.id === runCase.currentStageId);
    setStageIndex(currentIndex >= 0 ? currentIndex : 0);
    fieldValuesRef.current = runCase.values;
    setFieldValues(runCase.values);
    setTerminalStatus(toTerminalStatus(runCase));
    // A web-service step captures the new Employee ID into the case as a variable (AC9).
    if (runCase.variables.employeeId) setEmployeeId(runCase.variables.employeeId);
    setFormFeedback(null);
    setFormFieldErrors({});
    setAppliedCase(runCase);
  }, [runCase]);

  const stages = useMemo(() => definition?.canvases ?? [], [definition]);
  // The route panel stands in for the stage the case is on, without moving the case or the rail.
  const routeIndex = routedFlow ? stages.findIndex((stage) => stageKeyOf(stage) === PIF_STAGE_KEYS.route) : -1;
  const shownIndex = routeIndex >= 0 ? routeIndex : stageIndex;
  const currentStage = stages[shownIndex];
  const isPif = Boolean(definition?.isFlagship);
  const currentStageKey = currentStage ? stageKeyOf(currentStage) : "";
  const isPifScreen = isPif && (Object.values(PIF_STAGE_KEYS) as string[]).includes(currentStageKey);
  // The PIF (a case, or one about to be started) uses the identity lookup and PostIF reference data,
  // and saves pif-1's and pif-3's answers by field key. A new hire's SSN goes to their applicant
  // record after pif-1's submit (decision P4, useWorkflowPifApplicant).
  const lookupPifIdentityLive = useWorkflowPifIdentityLookup();
  // A PIF case reopened past pif-1 (a refresh, or opened from Instances) has no identity in this
  // session: it is rebuilt from what the case saved (AC11).
  const resumed = useWorkflowPifResumedIdentity({
    runCase,
    enabled: isPif && !identity,
    lookupIdentity: lookupPifIdentityLive,
  });
  const caseIdentity = identity ?? resumed.identity;
  // The navigation trace: opening the case, and each stage landed on (decision 6.3).
  useWorkflowRunTrace(runCase, source.fromMyWork ? ModuleNames.WorkflowMyWork : ModuleNames.WorkflowInstances);

  // A real case's open form stage persists as it is typed: a generic form, and pif-3 (by field key,
  // through the same save / submit). pif-1 writes in one submit on Continue instead (savesPifStart);
  // the other stage types act through their own live components.
  const isPifDetails = isPif && currentStageKey === PIF_STAGE_KEYS.details;
  const persistsForm = Boolean(
    runCase && !terminalStatus && currentStage?.type === "form" && (!isPifScreen || isPifDetails),
  );
  const savesPifStart = Boolean(
    runCase && !terminalStatus && isPif && currentStage?.type === "form" && currentStageKey === PIF_STAGE_KEYS.start,
  );
  const pifApplicant = useWorkflowPifApplicant();
  const formSave = useWorkflowFormSave(
    runCase && (persistsForm || savesPifStart)
      ? { instanceId: runCase.instanceId, stageId: Number(currentStage.id), rowVersionTag: runCase.rowVersionTag }
      : null,
  );

  // pif-1 is the case's first write: its answers go in one form/submit, which also runs pif-2 (the
  // engine routes on pif.lookupFlow) and lands the case on the next stage. A new hire's SSN then
  // goes to their applicant record (decision P4) — after the submit, because the server only takes
  // it once the case has saved pif.lookupFlow = new. Continue stays busy until both are done.
  const { submit: submitForm } = formSave;
  const { save: savePifApplicant } = pifApplicant;
  const runInstanceId = runCase?.instanceId ?? null;
  const persistPifStart = useCallback(
    async (answers: PifStartAnswers) => {
      setFormFeedback(null);
      setPifStartSaving(true);
      const outcome = await submitForm(toKeyedValueInputs(currentStage, toPifStartValues(answers)));
      if (!outcome) {
        setPifStartSaving(false);
        return;
      }
      if (outcome.kind === "submitted" && answers.newHireSsn && runInstanceId) {
        await savePifApplicant(runInstanceId, answers.newHireSsn, outcome.rowVersionTag ?? "");
      }
      setPifStartSaving(false);
      switch (outcome.kind) {
        case "submitted":
          setPifStartAnswers(null);
          setRoutedFlow(answers.match.flow);
          setFormFeedback((await refreshCase()) ? null : { kind: "reload-failed" });
          return;
        case "not-found":
          setPifStartAnswers(null);
          await refreshCase();
          return;
        case "invalid":
          setFormFeedback({
            kind: "invalid",
            messages: [...Object.values(outcome.fieldErrors), ...outcome.messages],
          });
          return;
        case "forbidden":
          setFormFeedback({ kind: "forbidden" });
          return;
        case "failed":
          setFormFeedback({ kind: "submit-failed" });
          return;
        default:
          // conflict — the save hook's status carries the banner.
          setFormFeedback(null);
      }
    },
    [currentStage, refreshCase, runInstanceId, savePifApplicant, setPifStartAnswers, submitForm],
  );

  // One trigger for every pif-1 submit: Continue on a not-yet-started PIF (once its case has
  // started and loaded), Continue on a case already waiting at pif-1, and Retry.
  useEffect(() => {
    if (!pifStartAnswers || !savesPifStart || sentPifStartRef.current === pifStartAnswers) return;
    sentPifStartRef.current = pifStartAnswers;
    setIdentity(pifStartAnswers.match);
    void persistPifStart(pifStartAnswers);
  }, [pifStartAnswers, savesPifStart, persistPifStart]);

  // pif-3's answers by key, from the case's saved values (all stages').
  const pifDetailsStage = useMemo(
    () => stages.find((stage) => stageKeyOf(stage) === PIF_STAGE_KEYS.details),
    [stages],
  );
  const pifDetailsValues = useMemo<PifDetailsValues>(
    () => toPifValuesByKey(pifDetailsStage ? [pifDetailsStage] : [], fieldValues),
    [pifDetailsStage, fieldValues],
  );
  // A new hire on pif-3 whose case has no applicant record (the SSN was lost to a refresh after a
  // failed save) re-enters it before the PIF can go for approval (Pooja, 2026-10-08). GET instance
  // returns pif.applicantId masked, so "has a value" is all the screen can — and needs to — know.
  const applicantIdField = useMemo(
    () =>
      stages
        .find((stage) => stageKeyOf(stage) === PIF_STAGE_KEYS.start)
        ?.fields.find((field) => field.fieldKey === PIF_VARIABLES.applicantId),
    [stages],
  );
  const missingApplicantRecord = Boolean(
    isPifDetails &&
      caseIdentity?.flow === "new" &&
      applicantIdField &&
      !fieldValues[applicantIdField.id],
  );
  // When pif-1's save failed in this session, its banner's Retry resends the SSN still held, so the
  // card would only ask for what is already here. The card shows its own spinner while it saves.
  const asksToReenterSsn = missingApplicantRecord && pifApplicant.status !== "failed";
  const handleReenterSsn = async (ssn: string): Promise<boolean> => {
    if (!runCase) return false;
    const saved = await pifApplicant.save(runCase.instanceId, ssn, runCase.rowVersionTag);
    if (saved) setFormFeedback((await refreshCase()) ? null : { kind: "reload-failed" });
    return saved;
  };

  // pif-3's display names: once pif-3 is submitted, the server writes them from the IDs the case saved
  // (Pooja, 2026-10-08). The case's creator asks for that on every load past pif-3 that still lacks
  // them, so a failed attempt is retried on the next load; approvers see "Loading…" meanwhile.
  const pastPifDetails = Boolean(
    isPif &&
      runCase &&
      isOpenCase(runCase) &&
      currentStageKey &&
      !([PIF_STAGE_KEYS.start, PIF_STAGE_KEYS.route, PIF_STAGE_KEYS.details] as string[]).includes(currentStageKey),
  );
  const pifNamesMissing = PIF_NAMED_IDS.some(([idKey, nameKey]) => pifDetailsValues[idKey] && !pifDetailsValues[nameKey]);
  const { refreshCaseQuietly: reloadCaseQuietly } = source;
  const refreshCaseQuietly = useCallback(() => {
    void reloadCaseQuietly();
  }, [reloadCaseQuietly]);
  useWorkflowPifDisplayNames({
    instanceId: runCase?.instanceId ?? null,
    rowVersionTag: runCase?.rowVersionTag ?? null,
    enabled: pastPifDetails && pifNamesMissing && userId !== null && runCase?.startedBy === userId,
    onSaved: refreshCaseQuietly,
  });

  // A real case reads the names pif-3 saved with its IDs.
  const applicant = useMemo(() => (isPif ? toPifApplicantSummary(pifDetailsValues) : null), [isPif, pifDetailsValues]);

  const handleStartContinue = (answers: PifStartAnswers) => {
    if (answers.match.flow === "blocked") return;
    setIdentity(answers.match);
    // Start the case now if it hasn't been (past the HR gate, AC2), then save these answers to it —
    // see the pif-1 submit effect above.
    setPifStartAnswers(answers);
  };

  const handleRetryPifStart = () => {
    if (pifStartAnswers) setPifStartAnswers({ ...pifStartAnswers });
  };

  /** Resends the new hire's SSN still held from pif-1, with the case's current tag. */
  const handleRetryPifApplicant = async () => {
    if (!runCase) return;
    if (await pifApplicant.retry(runCase.rowVersionTag)) {
      setFormFeedback((await refreshCase()) ? null : { kind: "reload-failed" });
    }
  };

  // The route panel only ever stands in for the stage the case is on, so Continue just closes it.
  const handleRouteContinue = () => setRoutedFlow(null);

  /** A change to one or more of the current stage's fields, by field id. */
  const handleFieldsChange = (patch: Record<string, string>) => {
    const next = { ...fieldValuesRef.current, ...patch };
    fieldValuesRef.current = next;
    setFieldValues(next);
    if (!persistsForm) return;
    // Values are committed as they are typed, so a failed save or a refresh loses nothing.
    formSave.queueSave(toStageValueInputs(currentStage, next));
    if (Object.keys(patch).some((fieldId) => formFieldErrors[fieldId])) {
      setFormFieldErrors((current) => {
        const remaining = { ...current };
        Object.keys(patch).forEach((fieldId) => delete remaining[fieldId]);
        return remaining;
      });
    }
  };

  const handleFieldChange = (fieldId: string, value: string) => handleFieldsChange({ [fieldId]: value });

  const handlePifDetailsChange = (patch: PifDetailsValues) => {
    if (isPifDetails) handleFieldsChange(toFieldIdPatch(currentStage, patch));
  };

  const handleSaveDraft = async () => {
    if (persistsForm && !(await formSave.saveNow(toStageValueInputs(currentStage, fieldValues)))) {
      return;
    }
    showToaster("Draft saved", SEVERITIES.SUCCESS);
  };

  const handleRetrySave = () => {
    void formSave.saveNow(toStageValueInputs(currentStage, fieldValues));
  };

  const handleReloadCase = async () => {
    setFormFeedback((await refreshCase()) ? null : { kind: "reload-failed" });
  };

  const handleFormContinue = async () => {
    if (!persistsForm) return;
    // A new hire is not sent for approval until their SSN is on the applicant record (P4).
    if (isPifDetails && (pifApplicant.status === "saving" || pifApplicant.status === "failed")) {
      showToaster("Save the applicant's SSN first — use Retry above", SEVERITIES.INFO);
      return;
    }
    const outcome = await formSave.submit(toStageValueInputs(currentStage, fieldValues));
    if (!outcome) return;
    setFormFieldErrors(outcome.kind === "invalid" ? outcome.fieldErrors : {});
    switch (outcome.kind) {
      case "submitted":
      case "not-found":
        // The engine has moved the case (or it is gone): reload shows where it is now.
        await handleReloadCase();
        return;
      case "invalid":
        setFormFeedback({ kind: "invalid", messages: outcome.messages });
        return;
      case "forbidden":
        setFormFeedback({ kind: "forbidden" });
        return;
      case "failed":
        setFormFeedback({ kind: "submit-failed" });
        return;
      default:
        // conflict — the save hook's status carries the banner.
        setFormFeedback(null);
    }
  };

  // A recorded decision moves the case (next stage, or a reject/cancel terminal) or leaves it
  // waiting on other approvers; reloading shows which. A decision that ends the case keeps its
  // stamp for the terminal screen.
  const handleApprovalDecided = async (decided: WorkflowApprovalDecided): Promise<boolean> => {
    if (decided.stageResolved && decided.decision !== "accept") {
      setDecisionReason(decided.reason);
      setDecisionStamp(decided.stamp ? { by: decided.stamp.name, at: decided.stamp.at } : null);
    }
    return refreshCase();
  };

  const handleStartAnother = () => {
    if (!runCase) return;
    // A fresh case through the normal start flow. The PIF starts again by Token, from its latest
    // published version, so a user without Library access can; any other definition reuses the same
    // version.
    setAppliedCase(null);
    source.startAnother(isPif ? { token: PIF_DEFINITION_TOKEN } : { definitionId: runCase.definition.id });
    setStageIndex(0);
    setIdentity(null);
    setEmployeeId(null);
    fieldValuesRef.current = {};
    setFieldValues({});
    setTerminalStatus(null);
    setDecisionReason("");
    setDecisionStamp(null);
    setRoutedFlow(null);
    sentPifStartRef.current = null;
    pifApplicant.clear();
  };

  const sourceScreen = renderWorkflowRunSourceScreen({
    source,
    appliedInstanceId: appliedCase?.instanceId ?? null,
    canEditBuilder,
  });
  if (sourceScreen || !definition || !currentStage) return sourceScreen;

  // A PIF still on its HR gate has no case yet, so there is no reference number to show. A case
  // with no RefNo yet is named by its id, as its share link is.
  const reference = runCase ? (runCase.refNo ?? `#${runCase.instanceId}`) : "Not started";
  const statusLabel = toStatusLabel(terminalStatus, isPifPreStart);
  const { stepStates, progressCount } = toPipeline(stages, stageIndex, terminalStatus, runCase);
  const isReadOnlyCase = Boolean(runCase && finishedOnOpen === runCase.instanceId);
  // Decision 6.4: pif-7 (Create Employee) failed but its onFailure let the case continue. The
  // seeded PIF cancels on that failure instead, so this only shows if the definition is changed.
  const pifEmployeeNotCreated = Boolean(
    isPif &&
      runCase &&
      runCase.status !== "cancelled" &&
      webServiceStageFailed(
        runCase,
        stages.find((stage) => stageKeyOf(stage) === PIF_LATER_STAGE_KEYS.createEmployee),
      ),
  );
  const formBanner =
    persistsForm || savesPifStart ? (
      <WorkflowRunFormBanner
        saveStatus={formSave.saveStatus}
        feedback={formFeedback}
        onReload={handleReloadCase}
        onRetrySave={handleRetrySave}
        onRetrySubmit={savesPifStart && pifStartAnswers ? handleRetryPifStart : undefined}
      />
    ) : undefined;
  // Decision 6.5: /wf/{RefNo}. Opening it still needs sign-in and access to the case. A case with no
  // RefNo yet falls back to naming it by id.
  const shareUrl = runCase
    ? runCase.refNo
      ? `${window.location.origin}${WORKFLOW_SHARE_LINK_PREFIX}${encodeURIComponent(runCase.refNo)}`
      : `${window.location.origin}/workflow/run?instanceId=${runCase.instanceId}`
    : undefined;
  // A button's write, then the reload that brings the next stage: the stage on screen is stale for
  // both, so the page is covered (full screen, like the app's other pages) rather than left
  // clickable with nothing saying it is working.
  const stageBusy = source.caseRefreshing || formSave.submitting || pifStartSaving;

  return (
    <WorkflowRunPage reference={reference}>
      <BackdropProgressBar loading={stageBusy} />
      <section className="workflow-run__layout" aria-label="Workflow runtime" aria-busy={stageBusy}>
        <Box className="workflow-run__main">
          {source.fromBuilder ? (
            <Box className="workflow-run__mode" role="group" aria-label="Editor mode">
              <Button
                component={RouterLink}
                to={`/workflow/builder?definitionId=${definition.id}`}
                startIcon={<AccountTreeOutlined />}
              >
                Builder
              </Button>
              <Button className="workflow-run__mode-active" startIcon={<PlayArrowOutlined />} aria-pressed>
                Preview / Run
              </Button>
            </Box>
          ) : (
            <Button
              className="workflow-run__back"
              component={RouterLink}
              to={source.fromInstances ? "/workflow/instances" : "/workflow/library"}
              startIcon={<ArrowBack />}
              disableRipple
            >
              {source.fromInstances ? "Back to Instances & My Work" : "Back to Library"}
            </Button>
          )}

          {runCase?.isTest && (
            <WorkflowTestRunBar
              instanceId={runCase.instanceId}
              startedBy={runCase.startedBy}
              open={isOpenCase(runCase)}
              onDiscarded={refreshCase}
            />
          )}
          {isPif && runCase && !terminalStatus && !asksToReenterSsn && (
            <WorkflowPifApplicantBanner status={pifApplicant.status} onRetry={handleRetryPifApplicant} />
          )}
          {asksToReenterSsn && (
            <WorkflowPifReenterSsnCard
              onSave={handleReenterSsn}
              saving={pifApplicant.status === "saving"}
              refused={pifApplicant.status === "refused"}
            />
          )}
          {/* P3: a reopened case's re-check by Employee ID explains a failure as pif-1 does. */}
          {isPif && !identity && resumed.lookupError && (
            <Alert
              severity="warning"
              className="workflow-run__alert"
              action={
                resumed.lookupError === "forbidden" ? undefined : (
                  <Button color="inherit" size="small" onClick={resumed.retry}>
                    Retry
                  </Button>
                )
              }
            >
              {PIF_LOOKUP_ERROR_MESSAGES[resumed.lookupError]}
            </Alert>
          )}
          {pifEmployeeNotCreated && (
            <Alert severity="warning" className="workflow-run__alert">
              Employee record wasn&apos;t created automatically. HR has been notified.
            </Alert>
          )}
          <Card className="workflow-run__stage" variant="outlined">
            {terminalStatus ? (
              <CardContent className="workflow-run__stage-body">
                <WorkflowTerminalStage
                  status={terminalStatus}
                  reference={reference}
                  definitionName={definition.name}
                  subjectLabel={isPif ? "PIF" : "Case"}
                  applicantName={applicant?.name || caseIdentity?.name || ""}
                  employeeId={employeeId}
                  // A rejection's note and approver come from the case's saved approvals, so anyone
                  // reopening it sees them; decisionReason only holds what this session decided or
                  // typed. When the owner then closed the case with a reason of their own (this
                  // session's, else the case timeline's), that shows on its own line rather than
                  // replacing the approver's note.
                  reason={(terminalStatus === "rejected" && runCase?.decline?.reason) || decisionReason}
                  closeReason={rejectedCloseReason(terminalStatus, runCase, decisionReason)}
                  closedBy={runCase?.closedBy ?? undefined}
                  decisionBy={
                    decisionStamp?.by ??
                    (terminalStatus === "rejected" ? runCase?.decline?.by ?? undefined : undefined)
                  }
                  decidedAt={decisionStamp?.at}
                  readOnly={isReadOnlyCase}
                  onStartAnother={handleStartAnother}
                  closedAs={
                    isPif &&
                    terminalStatus === "cancelled" &&
                    runCase?.closedFromStageKey === PIF_LATER_STAGE_KEYS.employeeNotCreated
                      ? PIF_EMPLOYEE_NOT_CREATED_END
                      : undefined
                  }
                  shareUrl={shareUrl}
                />
              </CardContent>
            ) : (
              <>
                <Box className="workflow-run__stage-header">
                  <Box className="workflow-run__stage-icon">
                    <WorkflowCanvasIcon type={currentStage.type} />
                  </Box>
                  <Box>
                    <Typography className="workflow-run__stage-type">
                      {WORKFLOW_CANVAS_LABELS[currentStage.type]} · Step {shownIndex + 1} of {stages.length}
                    </Typography>
                    <Typography component="h2" className="workflow-run__stage-title">
                      {currentStage.name}
                    </Typography>
                  </Box>
                  <Chip
                    className="workflow-run__stage-pill"
                    label={
                      (isPif ? PIF_STAGE_PILLS[currentStageKey] : undefined) ??
                      WORKFLOW_CANVAS_LABELS[currentStage.type]
                    }
                    size="small"
                  />
                </Box>
                <CardContent className="workflow-run__stage-body">
                  <WorkflowRunStageBody
                    stage={currentStage}
                    stageKey={currentStageKey}
                    isPifScreen={isPifScreen}
                    isPif={isPif}
                    reference={reference}
                    definitionName={definition.name}
                    identity={caseIdentity}
                    applicant={applicant}
                    fieldValues={fieldValues}
                    formBusy={formSave.submitting}
                    formFieldErrors={formFieldErrors}
                    formBanner={formBanner}
                    lookupIdentity={lookupPifIdentityLive}
                    pifStartSaving={pifStartSaving}
                    pifIdentityResolving={!identity && resumed.resolving}
                    onPifStartContinue={handleStartContinue}
                    onPifRouteContinue={handleRouteContinue}
                    pifDetails={{
                      values: pifDetailsValues,
                      onChange: handlePifDetailsChange,
                      onContinue: handleFormContinue,
                      onSaveDraft: handleSaveDraft,
                      busy: formSave.submitting,
                      continueBlocked: missingApplicantRecord,
                      serverErrors: isPifDetails ? toErrorsByKey(currentStage, formFieldErrors) : {},
                      banner: formBanner,
                    }}
                    onFieldChange={handleFieldChange}
                    onSaveDraft={handleSaveDraft}
                    onFormContinue={handleFormContinue}
                    liveCase={
                      runCase && !isPifScreen
                        ? {
                            instanceId: runCase.instanceId,
                            stageId: Number(currentStage.id),
                            rowVersionTag: runCase.rowVersionTag,
                            onChanged: refreshCase,
                            onRefreshQuietly: reloadCaseQuietly,
                            tasks: runCase.tasks.filter((task) => String(task.stageId) === currentStage.id),
                            pausedUntil: runCase.pausedUntil,
                            onCancelled: setDecisionReason,
                            canClose: runCase.canClose,
                            ownerName: runCase.ownerName,
                            decline: runCase.decline,
                          }
                        : undefined
                    }
                    liveApproval={
                      runCase && currentStage.type === "approval"
                        ? {
                            target: {
                              instanceId: runCase.instanceId,
                              stageId: Number(currentStage.id),
                              rowVersionTag: runCase.rowVersionTag,
                            },
                            onDecided: handleApprovalDecided,
                            onReloadCase: refreshCase,
                          }
                        : undefined
                    }
                  />
                </CardContent>
              </>
            )}
          </Card>
        </Box>

        <WorkflowRunRail
          reference={reference}
          definitionLabel={`${definition.name} · v${definition.version}`}
          statusLabel={statusLabel}
          statusModifier={terminalStatus ?? "active"}
          stages={stages}
          stepStates={stepStates}
          progressLabel={`(${progressCount}/${stages.length})`}
          history={runCase?.history}
        />
      </section>
    </WorkflowRunPage>
  );
};

export default WorkflowRun;
